import React, { useState, useEffect } from 'react';
import moize from 'moize';
import { Topic } from 'discourse-js';
import { useSelector, useDispatch } from 'react-redux';

import {
  Flex,
  Text,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  ModalFooter,
  ModalHeader,
  ModalCloseButton,
  Button,
  Collapse,
  MdIcon,
  Spinner,
  VStack,
  useToast,
} from '@workshop/ui';

import navRoutes from 'navigation/Routes';

import { JournalEntry } from 'types/cms';
import { GlobalState } from 'types';
import { Dispatch } from 'types';
import { discourseActions } from 'redux/actions/common';
import { journalActions } from 'redux/actions/learner';

import {
  CohortMember,
  JournalUnitSummary,
  AugmentedSessionSummary,
} from './types';

interface JournalTable {
  members: CohortMember[];
  journalUnits: JournalUnitSummary[];
  parentDiscourseCategoryId?: number;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  members: CohortMember[];
  journalUnits: JournalUnitSummary[];
  parentDiscourseCategoryId?: number;
}

const findJournalEntries = moize(
  (member: CohortMember, session: number) =>
    member.journalEntries?.filter(
      (entry) => entry.destinationObjectId === session
    ) || []
);

const getPageFromUrl = (url: string): number | null => {
  const match = url.match(/[?&]page=(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

const findDiscourseTopics = moize(
  (
    memberId: number,
    discourseCategoryId: number | null | undefined,
    session: AugmentedSessionSummary,
    unit: JournalUnitSummary,
    categoryTopics: Readonly<Partial<Topic>[]>
  ): Topic[] => {
    if (discourseCategoryId === null || discourseCategoryId === undefined) {
      return [];
    }
    return (
      categoryTopics.filter((topic): topic is Topic => {
        const posters = (topic as any)?.posters;
        if (
          !topic ||
          !posters ||
          topic.categoryId !== discourseCategoryId ||
          !posters.some(
            (p: any) =>
              p.userId === memberId && p.description === 'Original Poster'
          )
        ) {
          return false;
        }
        if (!topic.tags) return false;
        const unitTag = `unit-${unit.index + 1}`;
        const moduleTag = `module-${session.index + 1}`;
        const topicTags = topic.tags as string[];
        return topicTags.includes(unitTag) && topicTags.includes(moduleTag);
      }) || []
    );
  }
);

const findOrphanedDiscourseTopics = moize(
  (
    memberId: number,
    discourseCategoryId: number | null | undefined,
    journalEntries: JournalEntry[],
    session: AugmentedSessionSummary,
    unit: JournalUnitSummary,
    categoryTopics: Readonly<Partial<Topic>[]>
  ): Topic[] => {
    const sessionTopics = findDiscourseTopics(
      memberId,
      discourseCategoryId,
      session,
      unit,
      categoryTopics
    );
    const journalTopicIds = new Set(
      journalEntries
        .filter((entry) => entry.destinationObjectId === session.id)
        .map((entry) => entry.discourseTopicId)
    );
    return sessionTopics.filter((topic) => !journalTopicIds.has(topic.id));
  }
);

const findUnassignedDiscourseTopics = moize(
  (
    memberId: number,
    discourseCategoryId: number | null | undefined,
    journalUnits: JournalUnitSummary[],
    categoryTopics: Readonly<Partial<Topic>[]>
  ): Topic[] => {
    if (discourseCategoryId === null || discourseCategoryId === undefined) {
      return [];
    }

    const sessionTagPairs = new Set<string>();
    journalUnits.forEach((unit) => {
      unit.modules.forEach((module) => {
        sessionTagPairs.add(
          `unit-${unit.index + 1}|module-${module.index + 1}`
        );
      });
    });

    return (
      categoryTopics.filter((topic): topic is Topic => {
        const posters = (topic as any)?.posters;
        if (
          !topic ||
          !posters ||
          topic.categoryId !== discourseCategoryId ||
          !posters.some((p: any) => p.userId === memberId)
        ) {
          return false;
        }

        if (!topic.tags || topic.tags.length === 0) return true;

        const topicTags = topic.tags as string[];
        const tagNames = new Set(topicTags);

        for (const pair of sessionTagPairs) {
          const [unitTag, moduleTag] = pair.split('|');
          if (tagNames.has(unitTag) && tagNames.has(moduleTag)) {
            return false;
          }
        }

        return true;
      }) || []
    );
  }
);

interface JournalEntryRowProps {
  member: CohortMember;
  idx: number;
  sessions: AugmentedSessionSummary[];
  journalUnits: JournalUnitSummary[];
  isLoadingCategory: boolean;
  categoryTopics: Readonly<Partial<Topic>[]>;
  sessionUnitMap: Map<number, JournalUnitSummary>;
  expandedEntries: { [key: string]: boolean };
  handleToggleEntries: (key: string) => void;
  expandedOrphaned: { [key: string]: boolean };
  handleToggleOrphaned: (key: string) => void;
  expandedUnassigned: { [key: string]: boolean };
  handleToggleUnassigned: (key: string) => void;
  linkedTopicIds: Set<number>;
  linkingTopicId: number | null;
  onLinkTopic: (topicId: number, moduleId: number, username: string) => void;
}

const JournalEntryRow: React.FC<JournalEntryRowProps> = ({
  member,
  idx,
  sessions,
  journalUnits,
  isLoadingCategory,
  categoryTopics,
  sessionUnitMap,
  expandedEntries,
  handleToggleEntries,
  expandedOrphaned,
  handleToggleOrphaned,
  expandedUnassigned,
  handleToggleUnassigned,
  linkedTopicIds,
  linkingTopicId,
  onLinkTopic,
}) => {
  return (
    <Tr key={`journal-row-${member.id}`}>
      <Td
        position="sticky"
        left={0}
        bg="background.default"
        zIndex={1}
        minW="150px"
        w="150px"
        borderRightWidth="1px"
        borderRightColor="gray.200"
      >
        {member.name}
      </Td>
      {sessions.map((session) => {
        const entries = findJournalEntries(member, session.id);
        const unit = sessionUnitMap.get(session.id);

        if (!unit) {
          return <Td key={`cell-${member.id}-${session.id}-error}`}>Error</Td>;
        }

        const topics = findDiscourseTopics(
          member.id,
          member.discourseCategoryId,
          session,
          unit,
          categoryTopics
        );
        const orphanedTopics = findOrphanedDiscourseTopics(
          member.id,
          member.discourseCategoryId,
          member.journalEntries || [],
          session,
          unit,
          categoryTopics
        ).filter((topic) => !linkedTopicIds.has(topic.id as number));
        const hasDiscrepancy = entries.length !== topics.length;
        const cellKey = `cell-${member.id}-${session.id}`;
        const entriesKey = `entries-${cellKey}`;
        const orphanedKey = `orphaned-${cellKey}`;

        return (
          <Td
            key={cellKey}
            valign="top"
            bg={
              !isLoadingCategory && hasDiscrepancy
                ? 'background.warningTint'
                : undefined
            }
            minW="170px"
          >
            <VStack spacing={1} align="stretch">
              {entries.length > 0 && (
                <Flex direction="column">
                  <Button
                    size="xs"
                    aria-label="Show Journal Entries"
                    leftIcon={<MdIcon name="Done" />}
                    colorScheme="green"
                    onClick={() => handleToggleEntries(entriesKey)}
                  >
                    {entries.length} Upload{entries.length === 1 ? '' : 's'}
                  </Button>
                  <Collapse in={expandedEntries[entriesKey]} animateOpacity>
                    <VStack
                      spacing={1}
                      align="stretch"
                      paddingX="2"
                      mt="1"
                      pb="1"
                      bg={
                        idx % 2 === 0
                          ? 'background.default'
                          : 'background.tint2'
                      }
                      rounded="md"
                    >
                      {entries.map((entry, i) => (
                        <Button
                          key={`link-${entry.id}`}
                          leftIcon={<MdIcon name="OpenInNew" />}
                          size="xs"
                          variant="ghost"
                          justifyContent="start"
                          onClick={() =>
                            window.open(
                              navRoutes.cms.cmsViewPost.path(
                                entry.discourseTopicId
                              ),
                              '_blank'
                            )
                          }
                        >
                          {`View Upload ${i + 1}`}
                        </Button>
                      ))}
                    </VStack>
                  </Collapse>
                </Flex>
              )}

              {orphanedTopics.length > 0 && (
                <Flex direction="column">
                  <Button
                    size="xs"
                    aria-label="Show Orphaned Topics"
                    leftIcon={<MdIcon name="LinkOff" />}
                    colorScheme="orange"
                    variant="outline"
                    onClick={() => handleToggleOrphaned(orphanedKey)}
                  >
                    {orphanedTopics.length} Unlinked
                  </Button>
                  <Collapse in={expandedOrphaned[orphanedKey]} animateOpacity>
                    <VStack
                      spacing={1}
                      align="stretch"
                      paddingX="2"
                      mt="1"
                      pb="1"
                      bg={
                        idx % 2 === 0
                          ? 'background.default'
                          : 'background.tint2'
                      }
                      rounded="md"
                    >
                      {orphanedTopics.map((topic, i) => (
                        <Flex
                          key={`orphan-link-${topic.id}`}
                          direction="column"
                          gap={0.5}
                        >
                          <Button
                            leftIcon={<MdIcon name="OpenInNew" />}
                            size="xs"
                            variant="ghost"
                            justifyContent="start"
                            title={topic.title}
                            onClick={() =>
                              window.open(
                                navRoutes.cms.cmsViewPost.path(
                                  topic.id as number
                                ),
                                '_blank'
                              )
                            }
                          >
                            {`View Upload ${entries.length + i + 1}`}
                          </Button>
                          <Button
                            leftIcon={<MdIcon name="Link" />}
                            size="xs"
                            colorScheme="green"
                            variant="outline"
                            justifyContent="start"
                            isLoading={linkingTopicId === topic.id}
                            onClick={() =>
                              onLinkTopic(
                                topic.id as number,
                                session.id,
                                member.username
                              )
                            }
                          >
                            Link Upload
                          </Button>
                        </Flex>
                      ))}
                    </VStack>
                  </Collapse>
                </Flex>
              )}

              {entries.length === 0 && topics.length === 0 && (
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  borderRadius="md"
                  whiteSpace="nowrap"
                  height={6}
                  px={2}
                >
                  <MdIcon
                    mr={1}
                    name="Error"
                    color="icon.muted"
                    fontSize="sm"
                  />
                  <Text fontSize="xs" color="text.muted">
                    No Uploads
                  </Text>
                </Flex>
              )}
            </VStack>
          </Td>
        );
      })}
      <Td key={`unassigned-cell-${member.id}`} valign="top" minW="170px">
        {(() => {
          const unassignedTopics = findUnassignedDiscourseTopics(
            member.id,
            member.discourseCategoryId,
            journalUnits,
            categoryTopics
          );
          const unassignedKey = `unassigned-${member.id}`;
          if (unassignedTopics.length === 0) {
            return null;
          }
          return (
            <Flex direction="column">
              <Button
                size="xs"
                aria-label="Show Unassigned Topics"
                leftIcon={<MdIcon name="HelpOutline" />}
                colorScheme="purple"
                variant="outline"
                onClick={() => handleToggleUnassigned(unassignedKey)}
              >
                {unassignedTopics.length} Unassigned
              </Button>
              <Collapse in={expandedUnassigned[unassignedKey]} animateOpacity>
                <VStack
                  spacing={1}
                  align="stretch"
                  paddingX="2"
                  mt="1"
                  pb="1"
                  bg={idx % 2 === 0 ? 'background.default' : 'background.tint2'}
                  rounded="md"
                >
                  {unassignedTopics.map((topic, i) => (
                    <Button
                      key={`unassigned-link-${topic.id}`}
                      leftIcon={<MdIcon name="OpenInNew" />}
                      size="xs"
                      variant="ghost"
                      justifyContent="start"
                      title={topic.title}
                      onClick={() =>
                        window.open(
                          navRoutes.cms.cmsViewPost.path(topic.id as number),
                          '_blank'
                        )
                      }
                    >
                      {`View Upload ${i + 1}`}
                    </Button>
                  ))}
                </VStack>
              </Collapse>
            </Flex>
          );
        })()}
      </Td>
    </Tr>
  );
};

export const JournalEntryTable: React.FC<JournalTable> = ({
  members,
  journalUnits,
  parentDiscourseCategoryId,
}) => {
  const dispatch: Dispatch = useDispatch();
  const toast = useToast();
  const categories = useSelector(
    (state: GlobalState) => state.discourse.categories
  );
  const [categoriesLoading, setCategoriesLoading] = useState<{
    [key: number]: boolean;
  }>({});
  const [linkedTopicIds, setLinkedTopicIds] = useState<Set<number>>(new Set());
  const [linkingTopicId, setLinkingTopicId] = useState<number | null>(null);

  const handleLinkTopic = async (
    topicId: number,
    moduleId: number,
    username: string
  ) => {
    setLinkingTopicId(topicId);
    try {
      const result: any = await dispatch(
        journalActions.linkDiscourseTopic({
          discourseTopicId: topicId,
          moduleId,
          username,
        })
      );

      if (result && !result.error) {
        setLinkedTopicIds((prev) => new Set(prev).add(topicId));
        toast({
          title: 'Upload linked',
          description:
            'A journal entry has been created for this upload. Any certificate or completion actions will follow automatically.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Failed to link upload',
          description:
            'The upload could not be linked. Please try again or check the student has progress for this session.',
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Failed to link upload',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setLinkingTopicId(null);
    }
  };
  const [categoryFetchStatus, setCategoryFetchStatus] = useState<{
    [key: number]: 'idle' | 'fetching' | 'complete';
  }>({});

  useEffect(() => {
    if (typeof parentDiscourseCategoryId !== 'number') {
      return;
    }

    const categoryIds = new Set(
      members
        .map((m) => m.discourseCategoryId)
        .filter((id): id is number => id !== null && id !== undefined)
    );

    categoryIds.forEach(async (subCatId) => {
      if (
        categoryFetchStatus[subCatId] === 'fetching' ||
        categoryFetchStatus[subCatId] === 'complete'
      ) {
        return;
      }

      setCategoriesLoading((prev) => ({ ...prev, [subCatId]: true }));
      setCategoryFetchStatus((prev) => ({ ...prev, [subCatId]: 'fetching' }));

      let currentPage = 0;
      let moreTopicsUrl:
        | string
        | null
        | undefined = `dummy?page=${currentPage}`;

      try {
        while (moreTopicsUrl) {
          const categoryData = await dispatch(
            discourseActions.getSubcategory(
              parentDiscourseCategoryId,
              subCatId,
              true,
              {
                order: 'created',
                page: currentPage,
              }
            )
          );

          const fetchedCategory = (categoryData as any)?.entities
            ?.discourseCategories?.[subCatId];

          if (!fetchedCategory?.topicList) {
            moreTopicsUrl = null;
            break;
          }

          moreTopicsUrl = fetchedCategory.topicList.moreTopicsUrl;
          currentPage = moreTopicsUrl
            ? getPageFromUrl(moreTopicsUrl) ?? currentPage + 1
            : 0;

          if (!moreTopicsUrl) {
            setCategoryFetchStatus((prev) => ({
              ...prev,
              [subCatId]: 'complete',
            }));
            break;
          }
        }
      } catch (error) {
        setCategoryFetchStatus((prev) => ({ ...prev, [subCatId]: 'complete' }));
      } finally {
        setCategoriesLoading((prev) => ({ ...prev, [subCatId]: false }));
        if (!moreTopicsUrl && categoryFetchStatus[subCatId] !== 'complete') {
          setCategoryFetchStatus((prev) => ({
            ...prev,
            [subCatId]: 'complete',
          }));
        }
      }
    });
  }, [
    members,
    dispatch,
    categories,
    categoryFetchStatus,
    parentDiscourseCategoryId,
  ]);

  const [expandedEntries, setExpandedEntries] = useState<{
    [key: string]: boolean;
  }>({});
  const handleToggleEntries = (key: string) => {
    setExpandedEntries((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const [expandedOrphaned, setExpandedOrphaned] = useState<{
    [key: string]: boolean;
  }>({});
  const handleToggleOrphaned = (key: string) => {
    setExpandedOrphaned((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const [expandedUnassigned, setExpandedUnassigned] = useState<{
    [key: string]: boolean;
  }>({});
  const handleToggleUnassigned = (key: string) => {
    setExpandedUnassigned((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const sessions: AugmentedSessionSummary[] = journalUnits.reduce(
    (acc, curr) => {
      return [...acc, ...curr.modules];
    },
    [] as AugmentedSessionSummary[]
  );

  const sessionUnitMap = new Map<number, JournalUnitSummary>();
  journalUnits.forEach((unit) => {
    unit.modules.forEach((module) => {
      sessionUnitMap.set(module.id, unit);
    });
  });

  const isLoading = Object.values(categoriesLoading).some((loading) => loading);

  return (
    <Table variant="striped" size="sm">
      <Thead>
        <Tr>
          <Th
            position="sticky"
            left={0}
            bg="background.default"
            _dark={{ bg: 'gray.700' }}
            zIndex={2}
            minW="150px"
            w="150px"
            borderRightWidth="1px"
            borderRightColor="gray.200"
          >
            {isLoading ? <Spinner color="common.primary" size="sm" /> : ' '}
          </Th>
          {journalUnits.map((unit, idx) => (
            <Th
              key={`unit-heading-${unit.id}`}
              colSpan={unit.modules.length}
              textAlign={unit.modules.length > 1 ? 'center' : 'left'}
              letterSpacing="normal"
              textTransform="none"
              fontWeight="bold"
              color="text.default"
              backgroundColor={
                idx % 2 === 0 ? 'background.tint3' : 'background.tint1'
              }
              borderTopRadius="md"
            >
              {`${unit.index + 1} • ${unit.title}`}
            </Th>
          ))}
          <Th
            key="unassigned-heading"
            letterSpacing="normal"
            textTransform="none"
            fontWeight="bold"
            color="text.default"
            backgroundColor="background.tint1"
            borderTopRadius="md"
            rowSpan={2}
            verticalAlign="bottom"
            pb={2}
            minW="170px"
          >
            &nbsp;
          </Th>
        </Tr>
        <Tr>
          <Th
            position="sticky"
            left={0}
            bg="background.default"
            _dark={{ bg: 'gray.700' }}
            zIndex={2}
            minW="150px"
            w="150px"
            borderRightWidth="1px"
            borderRightColor="gray.200"
          >
            &nbsp;
          </Th>
          {sessions.map((session) => {
            const unit = sessionUnitMap.get(session.id);
            return (
              <Th
                key={`session-heading-${session.id}`}
                letterSpacing="normal"
                textTransform="none"
                fontWeight="semibold"
                color="text.muted"
                minW="170px"
              >
                {`${unit ? `${unit.index + 1}.` : ''}${session.index + 1} • ${
                  session.title
                }`}
              </Th>
            );
          })}
        </Tr>
      </Thead>
      <Tbody>
        {members.map((member, idx) => {
          const discourseCategoryId = member.discourseCategoryId;
          const isLoadingCategory = discourseCategoryId
            ? categoriesLoading[discourseCategoryId]
            : false;
          const categoryData = discourseCategoryId
            ? categories[discourseCategoryId]
            : null;
          const categoryTopics: Readonly<Partial<Topic>[]> =
            categoryData?.topicList?.topics ?? [];

          return (
            <JournalEntryRow
              key={`journal-row-comp-${member.id}`}
              member={member}
              idx={idx}
              sessions={sessions}
              journalUnits={journalUnits}
              isLoadingCategory={isLoadingCategory}
              categoryTopics={categoryTopics}
              sessionUnitMap={sessionUnitMap}
              expandedEntries={expandedEntries}
              handleToggleEntries={handleToggleEntries}
              expandedOrphaned={expandedOrphaned}
              handleToggleOrphaned={handleToggleOrphaned}
              expandedUnassigned={expandedUnassigned}
              handleToggleUnassigned={handleToggleUnassigned}
              linkedTopicIds={linkedTopicIds}
              linkingTopicId={linkingTopicId}
              onLinkTopic={handleLinkTopic}
            />
          );
        })}
      </Tbody>
    </Table>
  );
};

export const JournalEntryModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  members,
  journalUnits,
  parentDiscourseCategoryId,
}) => {
  const [renderBody, setRenderBody] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => setRenderBody(true), 250);
    return () => {
      clearTimeout(timeout);
      setRenderBody(false);
    };
  }, [isOpen]);

  return (
    <Modal
      onClose={onClose}
      motionPreset="none"
      scrollBehavior="outside"
      isOpen={isOpen}
    >
      <ModalOverlay />
      <ModalContent maxW={{ base: '100vw', md: '90vw' }}>
        <ModalHeader>Student Uploads</ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowX="scroll" pl={0} pr={0}>
          {renderBody ? (
            <JournalEntryTable
              members={members}
              journalUnits={journalUnits}
              parentDiscourseCategoryId={parentDiscourseCategoryId}
            />
          ) : (
            <Flex p={6}>
              <Spinner color="common.primary" />
            </Flex>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
